import { orderFulfillmentAnalyticsRoutes } from "@redotech/redo-merchant-analytics-app/analytics/order-fulfillment/route";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { breadcrumbSlot } from "@redotech/redo-web/breadcrumb";
import { memo } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { OmsReactivationModal } from "../../../order/oms-reactivation-modal";
import { outboundLabelRoutes, wmsRoutes } from "../../../order/route";
import { TranslatedBreadcrumbLabel } from "../../../return/returns-breadcrumb-label";
import { carrierClaimsRoutes } from "../../../return/route";
import { defaultChildRoute } from "./legacy-redirects";
import {
  type RoutePageComponent,
  SessionReplayRoutePage,
} from "./session-replay-route-page";
import { isOmsSessionReplayRoute } from "./shipping-route-matching";
import { warehouseRoutes } from "./warehouse-routes";

const shippingBreadcrumb = breadcrumbSlot();
const SHIPPING_ROUTE_BASE = "/stores/:storeId/shipping";

const HIDE_PADDING_ROUTES: string[] = [
  "/batch-history",
  "/bulk-fulfillments",
  "/carrier-claims",
  "/consolidations",
  "/consolidations/*",
  "/eod-manifest",
  "/outbound-labels/*",
  "/outbound-labels/bad-warehouse-bins",
  "/wms/*",
  "/warehouse",
  "/warehouse/*",
  "/analytics/order-fulfillment",
];

function hidePadding(url: string): boolean {
  if (matchPath(`${SHIPPING_ROUTE_BASE}/outbound-labels/about`, url)) {
    return false;
  }
  return HIDE_PADDING_ROUTES.some(
    (route) => !!matchPath(`${SHIPPING_ROUTE_BASE}${route}`, url),
  );
}

const FULLSCREEN_ROUTES: string[] = [
  "/outbound-labels/personalized-slips/:personalizedSlipId",
  "/outbound-labels/personalized-inserts/:personalizedSlipId",
  "/outbound-labels/pick-sessions",
  "/outbound-labels/pick-sessions/:pickSessionId",
  "/outbound-labels/pick-sessions/:pickSessionId/tote-scanning",
  "/outbound-labels/pick-sessions/:pickSessionId/route",
  "/outbound-labels/create-pick-session",
  "/wms/work-orders/detail/:workOrderId",
  "/warehouse",
  "/warehouse/*",
];

function isFullscreenRoute(url: string): boolean {
  return FULLSCREEN_ROUTES.some(
    (route) => !!matchPath(`${SHIPPING_ROUTE_BASE}${route}`, url),
  );
}

function hideHeader(url: string): boolean {
  return isFullscreenRoute(url);
}

function hideNav(url: string): boolean {
  return isFullscreenRoute(url);
}

const ShippingRoutesShell = memo(function ShippingRoutesShell({
  Page,
}: {
  Page: RoutePageComponent;
}) {
  const location = useLocation();

  return (
    <>
      <SessionReplayRoutePage
        enabled={isOmsSessionReplayRoute(location.pathname)}
        hideHeader={hideHeader}
        hideNavbar={hideNav}
        hidePadding={hidePadding}
        Page={Page}
        productId="oms"
      />
      <OmsReactivationModal />
    </>
  );
});

export const shippingRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: shippingBreadcrumb, breadcrumbNavigate: false },
    async lazy() {
      const { Page } = await import("../../page");
      return {
        element: (
          <>
            <TranslatedBreadcrumbLabel
              breadcrumb={shippingBreadcrumb}
              getLabel={(t) => t("Shipping")}
            />
            <ShippingRoutesShell Page={Page} />
          </>
        ),
      };
    },
    children: [
      defaultChildRoute("outbound-labels"),
      {
        path: "analytics/order-fulfillment",
        handle: { breadcrumb: "Analytics", breadcrumbNavigate: false },
        children: orderFulfillmentAnalyticsRoutes,
      },
      { path: "carrier-claims", children: carrierClaimsRoutes },
      {
        path: "consolidations",
        handle: { breadcrumb: "Consolidations" },
        children: [
          {
            index: true,
            async lazy() {
              const { ConsolidationsPage } =
                // redont-allow-dynamic-import: route-level code splitting
                await import("@redotech/redo-merchant-fulfillment-app/wms/consolidation/consolidations-page");
              return { Component: ConsolidationsPage };
            },
          },
          {
            path: "views/:viewName",
            async lazy() {
              const { ConsolidationsPage } =
                // redont-allow-dynamic-import: route-level code splitting
                await import("@redotech/redo-merchant-fulfillment-app/wms/consolidation/consolidations-page");
              return { Component: ConsolidationsPage };
            },
          },
          {
            path: ":consolidationId",
            handle: { breadcrumb: "Consolidation" },
            async lazy() {
              const { ConsolidationDetailPage } =
                // redont-allow-dynamic-import: route-level code splitting
                await import("@redotech/redo-merchant-fulfillment-app/wms/consolidation/consolidation-detail-page");
              return { Component: ConsolidationDetailPage };
            },
          },
        ],
      },
      { path: "outbound-labels", children: outboundLabelRoutes },
      { path: "wms", children: wmsRoutes },
      { path: "warehouse", children: warehouseRoutes },
    ],
  },
];
